<!-- @catalog Microphone button that dictates spoken text into a field. -->
<template>
  <c-button-icon
    v-if="isSupported === true"
    flat
    size="sm"
    :icon="icon"
    :disable="disable === true || isTranscribing === true"
    :title="label"
    :aria-label="label"
    :color="buttonColor"
    @click="toggle"
  />
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import CButtonIcon from "shared/components/wrappers/button/CButtonIcon.vue";
import { useVoiceInput } from "shared/composables/useVoiceInput";
import { useNotifyStore } from "store/modules/notifyStore";

withDefaults(
  defineProps<{
    disable?: boolean;
  }>(),
  {
    disable: false,
  },
);

const emit = defineEmits<{
  transcript: [text: string];
}>();

const { t } = useI18n();
const { setNotifyError } = useNotifyStore();

const { isSupported, isRecording, isTranscribing, error, toggle } =
  useVoiceInput((text) => emit("transcript", text));

const icon = computed(() => {
  if (isTranscribing.value === true) {
    return "fa-solid fa-spinner fa-spin";
  }

  if (isRecording.value === true) {
    return "fa-solid fa-stop";
  }

  return "fa-solid fa-microphone";
});

const label = computed(() => {
  if (isTranscribing.value === true) {
    return t("pages.voiceInput.transcribing");
  }

  if (isRecording.value === true) {
    return t("pages.voiceInput.stop");
  }

  return t("pages.voiceInput.start");
});

// Quasar's own `.text-{color}` is `!important`, and CButton hardcodes
// `color="green"` ahead of its `v-bind="filteredAttrs"` — so a Tailwind text
// class here would lose and both states would render green. Passing `color`
// through attrs wins, because the v-bind is applied after.
const buttonColor = computed(() => {
  if (isRecording.value === true) {
    return "red";
  }

  return "grey-800";
});

// A denied microphone or a failed transcription is invisible otherwise: the
// field simply stays empty and the teacher has no idea why.
watch(error, (errorKey) => {
  if (errorKey === null) {
    return;
  }

  setNotifyError({
    error: new Error(t(`pages.voiceInput.${errorKey}`)),
    fallbackMessage: t("pages.voiceInput.transcriptionFailed"),
  });
});
</script>
